import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BedDouble, Landmark, Network, ScrollText } from "lucide-react";
import { useGuidedTour } from "@/lib/guidedTour";

const CARDS = [
  {
    id: "cost-roi", Icon: Landmark, title: "Cost Avoidance & ROI Generator",
    metrics: [
      { label: "Dollars saved", to: 4.2, decimals: 1, prefix: "$", suffix: "M" },
      { label: "Taxpayer ROI ratio", to: 7.4, decimals: 1, suffix: " : 1" },
      { label: "Emergency dispatch load", to: 38, prefix: "−", suffix: "%" },
    ],
    bars: [72, 64, 55, 48, 41, 34],
  },
  {
    id: "data-sharing", Icon: Network, title: "Inter-Agency Data Sharing & Compliance",
    metrics: [
      { label: "Encrypted data transfers", to: 18420 },
      { label: "Audit trail coverage", to: 100, suffix: "%" },
      { label: "Multi-agency coordination logs", to: 312 },
    ],
    bars: [30, 44, 52, 61, 70, 84],
  },
  {
    id: "bed-capacity", Icon: BedDouble, title: "Municipal Bed & Resource Capacity Tracking",
    metrics: [
      { label: "Real-time occupancy rate", to: 92, suffix: "%" },
      { label: "Bed availability velocity", to: 4.6, decimals: 1, suffix: " hrs" },
      { label: "Waitlist auto-matched", to: 41 },
    ],
    bars: [88, 91, 86, 93, 90, 92],
  },
  {
    id: "grant-ledger", Icon: ScrollText, title: "Grant Accountability & Reporting Ledger",
    metrics: [
      { label: "Fund allocation efficiency", to: 97.8, decimals: 1, suffix: "%" },
      { label: "Measurable outcomes tracked", to: 1284 },
      { label: "Zero-loss compliance log", to: 0, suffix: " lost" },
    ],
    bars: [40, 52, 63, 71, 82, 96],
  },
];

function Counter({ to, decimals = 0, prefix = "", suffix = "", duration = 1400 }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf; const t0 = performance.now();
    const tick = (t) => { const p = Math.min(1, (t - t0) / duration); setV(to * (1 - (1 - p) ** 3)); if (p < 1) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, duration]);
  return <>{prefix}{v.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}{suffix}</>;
}

function SectorCard({ card, i, sector }) {
  const { Icon } = card;
  return (
    <motion.article data-testid={`exchange-${sector}-card-${card.id}`}
      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.5 }}
      className="group rounded-2xl border border-[#D4AF37]/20 bg-[#120C1C]/80 backdrop-blur-md p-5 hover:border-[#D4AF37]/60 hover:-translate-y-0.5 transition-[border-color,transform] duration-300">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 shrink-0 rounded-xl bg-[#D4AF37]/12 border border-[#D4AF37]/30 flex items-center justify-center">
          <Icon className="w-5 h-5 text-[#F4D88A]" />
        </div>
        <div className="min-w-0">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#C9A227]">Card 0{i + 1}</div>
          <h3 className="text-white font-semibold leading-snug">{card.title}</h3>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-3 gap-2">
        {card.metrics.map((m, j) => (
          <div key={m.label} data-testid={`exchange-${sector}-metric-${card.id}-${j}`} className="rounded-lg bg-white/[0.03] border border-white/5 px-2.5 py-2">
            <div className="font-mono text-sm sm:text-lg font-bold text-[#F4D88A] tabular-nums break-words leading-tight"><Counter {...m} /></div>
            <div className="mt-0.5 text-[10px] leading-tight text-slate-400">{m.label}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-end gap-1.5 h-10" aria-hidden>
        {card.bars.map((h, j) => (
          <motion.div key={j} className="flex-1 rounded-sm bg-gradient-to-t from-[#C9A227]/40 to-[#F4D88A]"
            initial={{ height: 0 }} animate={{ height: `${h}%` }} transition={{ delay: 0.2 + j * 0.07, duration: 0.6, ease: "easeOut" }} />
        ))}
      </div>
    </motion.article>
  );
}

// Shared sector beat — metrics replay each time the beat is spotlighted.
export function SectorSection({ sector, beatId, eyebrow, title, cards, dockLabel, dockItems }) {
  const { isActive, current } = useGuidedTour();
  const [run, setRun] = useState(0);
  const live = isActive && current?.id === beatId;
  useEffect(() => { if (live) setRun((r) => r + 1); }, [live]);
  return (
    <section data-testid={`exchange-${sector}-section`} className="mt-8 rounded-3xl border border-[#D4AF37]/20 bg-[#0A0612] p-6 md:p-8">
      <div className="mb-6">
        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#C9A227]">{eyebrow}</div>
        <h2 className="mt-1 text-2xl md:text-3xl font-extrabold tracking-tight text-white">{title}</h2>
      </div>
      <div className="flex gap-6">
        <div key={run} className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-2 gap-4">
          {cards.map((c, i) => <SectorCard key={c.id} card={c} i={i} sector={sector} />)}
        </div>
        <aside data-testid={`exchange-${sector}-dock`} className="hidden xl:flex w-[440px] shrink-0 rounded-2xl border border-dashed border-[#D4AF37]/25 bg-[#D4AF37]/[0.03] p-6 flex-col justify-center">
          <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#C9A227]">{dockLabel}</div>
          <ul className="mt-4 space-y-3 text-sm text-slate-300">
            {dockItems.map((t) => (
              <li key={t} className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#D4AF37] shrink-0" />{t}</li>
            ))}
          </ul>
        </aside>
      </div>
    </section>
  );
}

// Beat 02 — Government & Municipalities.
export default function ExchangeGovSection() {
  return (
    <SectorSection sector="gov" beatId="beat02" eyebrow="// 02 · Sector" title="Government & Municipalities" cards={CARDS}
      dockLabel="What agencies get"
      dockItems={["Real-time cost avoidance tracking", "HIPAA-compliant inter-agency data sharing", "Live bed capacity across every regional facility", "Automated HUD-compatible grant compliance"]} />
  );
}
